import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from './ui/card'
import {
  Building2,
  Users,
  Calendar,
  BarChart3,
  ArrowRight,
  Clock,
} from 'lucide-react'
import { useSessionStore } from '../stores/useSessionStore'
import { useApplicationStore } from '../stores/useApplicationStore'

const sections = [
  {
    to: '/client',
    icon: Building2,
    title: 'Clients',
    description:
      'Manage client organizations, badge configurations, and work regions.',
    color: 'text-blue-600 bg-blue-50',
  },
  {
    to: '/identity',
    icon: Users,
    title: 'Identities',
    description:
      'Manage people, provisionings, access control, and attachments.',
    color: 'text-violet-600 bg-violet-50',
  },
  {
    to: '/event',
    icon: Calendar,
    title: 'Events',
    description:
      'Create events, configure roles, accreditation levels, and schedules.',
    color: 'text-emerald-600 bg-emerald-50',
  },
  {
    to: '/report',
    icon: BarChart3,
    title: 'Reports',
    description: 'Generate identity, schedule, and punch clock reports.',
    color: 'text-amber-600 bg-amber-50',
  },
]

export const Welcome: React.FC = () => {
  const { session } = useSessionStore()
  const { clients, events, refreshClients, refreshEvents } =
    useApplicationStore()

  useEffect(() => {
    refreshClients().catch(() => {})
    refreshEvents().catch(() => {})
  }, [])

  const activeClients = clients.filter((c) => !c.dateArchived).length
  const activeEvents = events.length
  const firstName = session.firstName || 'there'

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Hero */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          {getGreeting()}, {firstName}
        </h1>
        <p className="text-muted-foreground text-lg">
          Welcome to the AuthentiGATE Employee Management System.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeClients}</p>
                <p className="text-xs text-muted-foreground">Active Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
                <Calendar className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeEvents}</p>
                <p className="text-xs text-muted-foreground">Events</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-50">
                <Users className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {session.admin ? 'Admin' : 'User'}
                </p>
                <p className="text-xs text-muted-foreground">Your Role</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {new Date().toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </p>
                <p className="text-xs text-muted-foreground">Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Sections */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Quick Access</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sections.map((section) => {
            const Icon = section.icon
            return (
              <Link key={section.to} to={section.to} className="group">
                <Card className="h-full transition-all hover:shadow-md hover:border-primary/20">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${section.color}`}
                      >
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-lg">
                            {section.title}
                          </h3>
                          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {section.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
